import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, BadgeCheck, Phone, MessageCircle, Star, ShieldCheck, Zap, ClipboardCheck, UserRoundCog, Grid3x3, GraduationCap, Megaphone, UserCheck, Receipt } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Eyebrow } from "@/components/site/Common";
import { ProCard } from "@/components/site/ProCard";
import { ContactForm } from "@/components/site/ContactForm";
import { AppDownload } from "@/components/site/AppDownload";
import { QuickQuote } from "@/components/site/QuickQuote";
import { TRADES, CONTACT, IMG_HERO, IMG_TRAINING } from "@/lib/hz";
import { get } from "@/lib/api";

const usePros = () => useQuery({ queryKey: ["pros", "all"], queryFn: () => get("/pros"), retry: 1 });

const Hero = () => {
  const { data: pros } = usePros();
  const rated = (pros ?? []).filter((p) => p.reviews_count > 0);
  const avg = rated.length ? rated.reduce((a, p) => a + p.rating, 0) / rated.length : null;
  return (
    <section className="relative overflow-hidden bg-slate-950 text-white" data-testid="hero">
      <div className="pointer-events-none absolute -left-40 top-0 size-[38rem] rounded-full bg-orange-600/10 blur-3xl" />
      <div className="relative mx-auto grid max-w-6xl items-center gap-12 px-4 pb-24 pt-16 md:pb-28 md:pt-20 lg:grid-cols-[1.1fr_1fr]">
        <div className="hz-rise">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-xs font-semibold text-orange-200"><BadgeCheck className="size-3.5" />Profesionales verificados y formados</span>
          <h1 className="mt-5 font-heading text-4xl font-extrabold leading-[1.05] tracking-tight sm:text-5xl lg:text-6xl">
            El profesional que tu casa necesita, <span className="text-orange-500">rápido y con calidad garantizada.</span>
          </h1>
          <p className="mt-5 max-w-xl text-base text-slate-300 md:text-lg">Fontanería, electricidad, manitas, carpintería, pintura, suelos y mantenimiento. Pide presupuestos o elige directamente a tu profesional, paga con tarjeta y recibe tu factura con IVA al terminar.</p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Button asChild size="lg" className="h-12 rounded-xl px-6"><Link to="/crear-solicitud" data-testid="hero-quote-btn">Pedir presupuesto gratis <ArrowRight /></Link></Button>
            <Button asChild size="lg" variant="outline" className="h-12 rounded-xl border-white/25 bg-transparent px-6 text-white hover:bg-white/10 hover:text-white"><Link to="/register?role=profesional" data-testid="hero-pro-btn">Soy profesional</Link></Button>
          </div>
          <div className="mt-7 flex flex-wrap gap-x-6 gap-y-2 text-sm text-slate-400">
            {avg != null && <span data-testid="hero-live-rating" className="flex items-center gap-1.5"><Star className="size-4 fill-amber-400 text-amber-400" />{avg.toFixed(1).replace(".", ",")}/5 valoración media</span>}
            {pros && <span data-testid="hero-live-pros" className="flex items-center gap-1.5"><UserCheck className="size-4 text-orange-400" />{pros.length} profesionales en la app</span>}
            <span className="flex items-center gap-1.5"><ShieldCheck className="size-4 text-emerald-400" />Control de calidad en cada trabajo</span>
          </div>
        </div>
        <div className="relative hz-rise [animation-delay:150ms]">
          <img src={IMG_HERO} alt="Profesional de fontanería trabajando" className="aspect-[6/5] w-full rounded-3xl object-cover" />
          <div className="absolute -bottom-6 left-4 rounded-2xl bg-white p-4 text-slate-900 shadow-xl sm:left-auto sm:right-5">
            <div className="text-[11px] font-bold uppercase tracking-widest text-orange-600">¿Necesitas ayuda ahora?</div>
            <a href={`tel:${CONTACT.phone}`} data-testid="hero-phone" className="mt-1 flex items-center gap-2 font-heading text-xl font-extrabold"><Phone className="size-5 text-orange-600" />{CONTACT.phoneDisplay}</a>
            <a href={CONTACT.whatsappUrl} target="_blank" rel="noreferrer" data-testid="hero-whatsapp" className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-3 py-1.5 text-sm font-semibold text-white hover:bg-emerald-600"><MessageCircle className="size-4" />Escríbenos por WhatsApp</a>
          </div>
        </div>
      </div>
    </section>
  );
};

const Services = () => (
  <section className="mx-auto max-w-6xl px-4 py-20" id="servicios">
    <Eyebrow>Servicios</Eyebrow>
    <h2 className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">¿Qué necesitas arreglar?</h2>
    <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {TRADES.map((t, i) => (
        <Link key={t.id} to={`/profesionales?oficio=${t.id}`} data-testid={`service-card-${t.id}`}
          className={`group rounded-2xl border border-slate-200 bg-white p-5 transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 hover:border-orange-300 hover:shadow-md ${i === 0 ? "lg:col-span-2" : ""}`}>
          <span className={`grid size-10 place-items-center rounded-xl ${t.tile}`}><t.icon className="size-5" /></span>
          <div className="mt-4 font-heading text-lg font-bold text-slate-900">{t.name}</div>
          <p className="mt-1 text-sm text-slate-600">{t.desc}</p>
          <span className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-orange-600 opacity-0 transition-opacity group-hover:opacity-100">Ver profesionales <ArrowRight className="size-4" /></span>
        </Link>
      ))}
    </div>
  </section>
);

const WHY = [
  { icon: Zap, title: "Rapidez en el servicio", text: "Publicas tu trabajo y los profesionales de tu oficio te responden con presupuestos en poco tiempo." },
  { icon: ClipboardCheck, title: "Control de calidad", text: "Cada servicio se revisa con nuestra checklist y, si algo no está bien, abres una incidencia y te respondemos en 24 h." },
  { icon: UserRoundCog, title: "Sustitución urgente", text: "Si el profesional programado no puede acudir, lo sustituimos con urgencia por otro de la misma especialidad." },
  { icon: Grid3x3, title: "Mayor gama de servicios", text: "Fontanería, electricidad, manitas, carpintería, pintura, suelos y mantenimiento en una sola app." },
  { icon: GraduationCap, title: "Formación propia por oficio", text: "Formamos a cada profesional según su oficio para garantizar la calidad en los resultados." },
];

const Why = () => (
  <section className="bg-white py-20" data-testid="why-section">
    <div className="mx-auto max-w-6xl px-4">
      <div className="grid gap-10 lg:grid-cols-[1fr_1.6fr]">
        <div>
          <Eyebrow>Por qué HandyZoom</Eyebrow>
          <h2 className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Rápidos, revisados y siempre con un profesional disponible.</h2>
          <p className="mt-4 text-slate-600">Ofrecemos rapidez en el servicio, control de calidad en los servicios ofrecidos, sustitución de profesional por ausencia del programado con urgencia, mayor gama de servicios y formación propia a cada profesional según su oficio para garantizar la calidad en los resultados.</p>
        </div>
        <div className="grid gap-4 sm:grid-cols-2">
          {WHY.map((w, i) => (
            <div key={w.title} className={`rounded-2xl border border-slate-200 bg-slate-50 p-5 ${i === 4 ? "sm:col-span-2" : ""}`}>
              <w.icon className="size-6 text-orange-600" />
              <div className="mt-3 font-heading text-lg font-bold text-slate-900">{w.title}</div>
              <p className="mt-1 text-sm text-slate-600">{w.text}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const STEPS = [
  { icon: Megaphone, title: "Publica tu trabajo", text: "Describe lo que necesitas y los profesionales de ese oficio te envían presupuestos. Compara precio, plazo y valoraciones, y acepta el mejor." },
  { icon: UserCheck, title: "Elige directamente", text: "¿Ya sabes con quién quieres trabajar? Busca en el directorio y envía tu solicitud directa al profesional." },
  { icon: Receipt, title: "Factura y pago seguro", text: "Al acabar, el profesional emite la factura con IVA 21%. Pagas con tarjeta y la descargas en PDF." },
  { icon: ClipboardCheck, title: "Control de calidad", text: "Revisa el trabajo con nuestra checklist, valóralo y, si algo no está bien, abre una incidencia. Te respondemos en 24 h." },
];

const HowItWorks = () => (
  <section className="mx-auto grid max-w-6xl gap-10 px-4 py-20 lg:grid-cols-[1fr_1.5fr]" data-testid="how-section">
    <div>
      <Eyebrow>Cómo funciona</Eyebrow>
      <h2 className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">Dos formas de contratar. Tú eliges.</h2>
      <p className="mt-4 text-slate-600">Todo el proceso queda registrado: presupuesto, trabajo, factura y pago. Y al terminar, tú revisas la calidad.</p>
    </div>
    <div className="grid gap-4 sm:grid-cols-2">
      {STEPS.map((s, i) => (
        <div key={s.title} className="rounded-2xl border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between"><s.icon className="size-6 text-orange-600" /><span className="font-heading text-sm font-bold text-slate-300">0{i + 1}</span></div>
          <div className="mt-4 font-heading text-lg font-bold text-slate-900">{s.title}</div>
          <p className="mt-2 text-sm text-slate-600">{s.text}</p>
        </div>
      ))}
    </div>
  </section>
);

const FeaturedPros = () => {
  const { data, isError } = usePros();
  if (isError || !data?.length) return null;
  const top = [...data].sort((a, b) => b.rating * 10 + b.reviews_count - (a.rating * 10 + a.reviews_count)).slice(0, 3);
  return (
    <section className="mx-auto max-w-6xl px-4 pb-20" data-testid="featured-pros">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div><Eyebrow>En directo desde la app</Eyebrow><h2 className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Profesionales mejor valorados</h2></div>
        <Link to="/profesionales" data-testid="see-all-pros" className="inline-flex items-center gap-1 text-sm font-semibold text-orange-600">Ver todo el directorio <ArrowRight className="size-4" /></Link>
      </div>
      <div className="mt-8 grid gap-5 md:grid-cols-3">{top.map((p) => <ProCard key={p.id} pro={p} />)}</div>
    </section>
  );
};

const Training = () => (
  <section className="mx-auto max-w-6xl px-4 pb-20">
    <div className="grid overflow-hidden rounded-3xl bg-slate-950 md:grid-cols-2">
      <img src={IMG_TRAINING} alt="Profesionales de carpintería en formación" className="h-full min-h-64 w-full object-cover" />
      <div className="flex flex-col justify-center p-8 text-white md:p-12">
        <GraduationCap className="size-8 text-orange-500" />
        <h2 className="mt-4 font-heading text-3xl font-extrabold tracking-tight">Formación en Oficios para profesionales</h2>
        <p className="mt-3 text-slate-300">Cursos por oficio con lecciones cortas y preguntas de evaluación. Al completarlos consigues insignias que se muestran en tu perfil público y generan más confianza.</p>
        <Button asChild className="mt-6 w-fit rounded-lg"><Link to="/register?role=profesional" data-testid="training-join-btn">Únete como profesional</Link></Button>
      </div>
    </div>
  </section>
);

const Contact = () => (
  <section id="contacto" className="bg-white py-20" data-testid="contact-section">
    <div className="mx-auto grid max-w-6xl gap-10 px-4 lg:grid-cols-[1fr_1.2fr]">
      <div>
        <Eyebrow>Contacto</Eyebrow>
        <h2 className="mt-2 font-heading text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl">¿Prefieres que te llamemos?</h2>
        <p className="mt-4 text-slate-600">Déjanos tus datos y te ayudamos a encontrar al profesional adecuado, o a darte de alta si ofreces servicios.</p>
        <div className="mt-8 space-y-3">
          <a href={`tel:${CONTACT.phone}`} data-testid="contact-call-btn" className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4 hover:border-orange-300"><Phone className="size-5 text-orange-600" /><div><div className="font-bold">{CONTACT.phoneDisplay}</div><div className="text-xs text-slate-500">{CONTACT.hours}</div></div></a>
          <a href={CONTACT.whatsappUrl} target="_blank" rel="noreferrer" data-testid="contact-whatsapp-btn" className="flex items-center gap-3 rounded-2xl border border-slate-200 p-4 hover:border-emerald-300"><MessageCircle className="size-5 text-emerald-600" /><div><div className="font-bold">WhatsApp {CONTACT.whatsappDisplay}</div><div className="text-xs text-slate-500">Respuesta rápida</div></div></a>
        </div>
      </div>
      <ContactForm />
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <Hero />
      <QuickQuote />
      <Services />
      <Why />
      <HowItWorks />
      <FeaturedPros />
      <Training />
      <div className="mx-auto max-w-6xl px-4 pb-20"><AppDownload /></div>
      <Contact />
    </>
  );
}
